import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { BeispielEtikett } from "../../../components/Beispiel";
import { useStaffWorkspace } from "../../../app/session";
import { ErrorState, Loading, NoAccess } from "../../../components/States";
import { KopfMehr, MailAdresse, Money, PillView, SortTh, Toolbar, matchesSearch, useAction, useSorted, SortWahl } from "../../../components/ui";
import { Icon } from "../../../components/Icon";
import { api } from "../../../data";
import { inviteTalents } from "./data";
import { date } from "../../../lib/format";
import { useBase, usePlan, useTeamInfo } from "./hooks";
import { aktiveCreatorImTarif, bpLabel, creatorBelegungText, creatorLines, invitable, matchesCreatorFilter, ohneKursText, statusPills, type CreatorFilter, type TalentFull } from "./model";
import { DemoNote, HIDDEN_FINANCIALS, useSicht } from "./parts";
import { TalentDialog } from "./TalentDialog";
import { t, ta } from "../../../lib/sprache";
import { VoreinstellungenEintrag } from "../einstellungen/Voreinstellungen";
import { NurLesenHinweis, NurSchreiben, useNurLesen } from "../../../app/nurLesen";
import { creatorGrenzeText, testFreiText } from "../../../model/tarif";

/** Creator-Liste (11_app_agentur/creator-liste.html) mit Umfang des Altportals (AD-073, AD-074, AD-077, AD-078), dazu „Betreut von“. */

const FILTERS: { key: CreatorFilter; label: string }[] = [
  { key: "alle", get label() { return t("Alle", "All"); } },
  { key: "meine", get label() { return t("Meine", "Mine"); } },
  { key: "aktiv", get label() { return t("Aktiv", "Active"); } },
  { key: "eingeladen", get label() { return t("Eingeladen", "Invited"); } },
  { key: "ohne_zugang", get label() { return t("Ohne Zugang", "Without access"); } },
  { key: "archiviert", get label() { return t("Archiviert", "Archived"); } },
];

export function CreatorListe() {
  const ws = useStaffWorkspace();
  // K-1/K-2: Pause und Nur-Lesen
  const nurLesen = useNurLesen();
  const navigate = useNavigate();
  const sicht = useSicht();
  const { base, error, loading, noAccess, retry, refresh } = useBase(ws);
  const plan = usePlan(ws);
  const team = useTeamInfo(ws);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<CreatorFilter>("alle");
  const [edit, setEdit] = useState<TalentFull | null>(null);
  // Sammel-Einladung (PRAXIS-8): Auswahl je Zeile, nur Creator ohne Zugang mit E-Mail-Adresse
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [inviteResult, setInviteResult] = useState<{ invited: string[]; failed: { name: string; error: string }[] } | null>(null);
  const { busy, run } = useAction();

  const lines = useMemo(() => (base ? creatorLines(base, team.data ?? null) : []), [base, team.data]);
  const shown = lines.filter((l) =>
    matchesCreatorFilter(l, filter)
    && matchesSearch(`${l.name} ${l.talent.email ?? ""} ${l.talent.city ?? ""} ${l.talent.instagram_handle ?? ""} ${l.talent.nickname ?? ""} ${l.statusPill.label} ${l.access.pill.label} ${l.managers.join(" ")}`, search));
  const { sorted, sort, toggle } = useSorted(shown, {
    name: (l) => l.name,
    email: (l) => l.talent.email,
    status: (l) => l.statusPill.label,
    zugang: (l) => l.access.pill.label,
    provision: (l) => l.commissionBp,
    kooperationen: (l) => l.activeDeals,
    umsatz: (l) => l.yearGross,
  });

  if (error) return <ErrorState error={error} retry={retry} />;
  if (noAccess) return <NoAccess />;
  if (loading || !base) return <Loading />;

  const restricted = !ws.isChef && !ws.membership.sees_all_talents;
  const selectable = sorted.filter(invitable);
  const chosen = lines.filter((l) => selected.has(l.talent.id) && invitable(l));
  const toggleOne = (id: string, on: boolean) => setSelected((s) => { const n = new Set(s); if (on) n.add(id); else n.delete(id); return n; });
  const allOn = selectable.length > 0 && selectable.every((l) => selected.has(l.talent.id));
  const inviteChosen = () => void run("einladen", async () => {
    const r = await inviteTalents(api, chosen.map((l) => ({ id: l.talent.id, name: l.name })));
    setInviteResult(r);
    setSelected(new Set());
    refresh();
  });
  // K-592: Beispiel-Creator zählen nicht in den Tarif (wie die Datenbank)
  const active = aktiveCreatorImTarif(base.talents);
  const year = base.today.slice(0, 4);

  return (
    <main id="main" className="content tk-sd tk-sd-liste tk-sd-creator">
      <div className="pagehead">
        <div>
          <div className="eyebrow">{ta("Agentur-Arbeitsbereich", "Agency workspace", "Dein Creator-Konto", "Your creator account")}</div>
          <h1>Creator</h1>
          <p>{t("Wen du managst: Profil, Konditionen, Zugang zum Portal und der Stand je Creator.", "Who you manage: profile, terms, portal access and the status per creator.")}</p>
        </div>
        {/* D-1 (Inhaber 21.10.2026): Insights und Pitch-Listen stehen auch in der Seitenleiste, Import ist selten: hinter „Mehr“ */}
        <div className="row tk-kopf-aktionen">
          {/* Nur-Lesen (Test-Chat 04.11.2026): Anlegen entfällt ausdrücklich, nicht nur über die Regel in portal.css */}
          <NurSchreiben><Link className="btn primary" to="/agentur/creator/einladen" data-coco="creator-anlegen">{t("Creator anlegen", "Add creator")}</Link></NurSchreiben>
          <KopfMehr>
            <Link to="/agentur/creator/insights">Insights</Link>
            <Link to="/agentur/creator/pitch">{t("Pitch-Listen", "Pitch lists")}</Link>
            <NurSchreiben><Link to="/agentur/creator/import">{t("Importieren", "Import")}</Link></NurSchreiben>
            <VoreinstellungenEintrag modul="creator" />
          </KopfMehr>
        </div>
      </div>

      {restricted && (
        <div className="notice">
          {t("Manager · ", "Manager · ")}{ws.name} {t("· Nur zugewiesene Creator: ", "· Assigned creators only: ")}{lines.map((l) => l.name).join(", ") || t("noch keine", "none yet")}{t(". Creator, die du neu anlegst, werden dir zugewiesen.", ". Creators you create are assigned to you.")}</div>
      )}

      <Toolbar
        search={search}
        onSearch={setSearch}
        label={t("Liste durchsuchen", "Search list")}
        placeholder={t("Suchen …", "Search …")}
        filters={FILTERS.map((f) => ({ ...f, count: lines.filter((l) => matchesCreatorFilter(l, f.key)).length }))}
        filter={filter}
        onFilter={setFilter}
      >
        <button className="tk-sd-neu-laden" onClick={refresh}>{t("Aktualisieren", "Refresh")}</button>
      </Toolbar>

      {selectable.length > 0 && (
        <div className="row between tk-bulkbar" role="group" aria-label={t("Sammel-Einladung", "Bulk invitation")}>
          <span>{chosen.length ? t(`${chosen.length} ausgewählt`, `${chosen.length} selected`) : t(`${selectable.length} ${selectable.length === 1 ? t("Creator ohne Zugang lässt", "creator without access") : t("Creator ohne Zugang lassen", "creators without access")} sich einladen`, `${selectable.length} ${selectable.length === 1 ? t("Creator ohne Zugang lässt", "creator without access") : t("Creator ohne Zugang lassen", "creators without access")} can be invited`)}</span>
          <div className="row">
            <button type="button" onClick={() => setSelected(allOn ? new Set() : new Set(selectable.map((l) => l.talent.id)))}>{allOn ? t("Auswahl aufheben", "Clear selection") : t("Alle ohne Zugang auswählen", "Select all without access")}</button>
            {/* D-2: gefüllt erst, wenn etwas ausgewählt ist (sonst wäre er ein zweiter gefüllter Knopf neben „Creator anlegen“) */}
            <button type="button" className={chosen.length ? "primary" : undefined} disabled={!chosen.length || !!busy} aria-busy={busy === "einladen"} onClick={inviteChosen}>
              {busy === "einladen" ? t("Wird eingeladen …", "Inviting …") : chosen.length === 1 ? t("Ausgewählten einladen", "Invite selected") : t(`${chosen.length || ""} Ausgewählte einladen`, `Invite ${chosen.length || ""} selected`).trim()}
            </button>
          </div>
        </div>
      )}
      {inviteResult && (
        <div className="notice stack" role="status">
          <p><strong>{inviteResult.invited.length === 1 ? t("1 Einladung verschickt", "1 invitation sent") : t(`${inviteResult.invited.length} Einladungen verschickt`, `${inviteResult.invited.length} invitations sent`)}{inviteResult.failed.length ? t(`, ${inviteResult.failed.length} nicht`, `, ${inviteResult.failed.length} not`) : ""}.</strong> {t("Jede Person bekommt ihren eigenen Link per Mail.", "Each person gets their own link by email.")}</p>
          {inviteResult.failed.length > 0 && <ul>{inviteResult.failed.map((f) => <li key={f.name}>{f.name}: {f.error}</li>)}</ul>}
          <div className="row"><button type="button" onClick={() => setInviteResult(null)}>{t("Hinweis schließen", "Close notice")}</button></div>
        </div>
      )}

      {/* Paket 2: am Handy Karten wie die Kooperationsliste (tk-koop-karten), sonst Tabelle */}
      <SortWahl />
      {/* L4 (Gesamtnachtest 03.11.2026): ohne klebende Aktionsspalte, sie überdeckte „Umsatz netto“ bzw. „Kooperationen“; ist die
          Tabelle breiter als ihr Rahmen, rollt sie darin */}
      <div className="tablewrap tk-koop-karten tk-sd-creatorliste">
        <table>
          <caption className="sr-only">Creator</caption>
          <thead>
            <tr>
              {selectable.length > 0 && <th scope="col"><span className="sr-only">{t("Auswahl", "Selection")}</span></th>}
              <SortTh label="Creator" k="name" sort={sort} toggle={toggle} />
              <SortTh label={t("E-Mail", "Email")} k="email" sort={sort} toggle={toggle} className="tk-sd-c-mail" />
              <SortTh label="Status" k="status" sort={sort} toggle={toggle} className="tk-sd-c-status" />
              <SortTh label={t("Zugang", "Access")} k="zugang" sort={sort} toggle={toggle} />
              {sicht.amounts && <SortTh label={t("Provision", "Commission")} k="provision" sort={sort} toggle={toggle} />}
              <SortTh label={t("Kooperationen", "Deals")} k="kooperationen" sort={sort} toggle={toggle} money />
              {sicht.financials && <SortTh label={t("Umsatz netto", "Net revenue")} k="umsatz" sort={sort} toggle={toggle} money className="tk-sd-c-umsatz" />}
              <th scope="col"><span className="sr-only">{t("Aktionen", "Actions")}</span></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((l) => {
              const meta = [l.talent.talent_number ? t(`Nr. ${l.talent.talent_number}`, `No. ${l.talent.talent_number}`) : null, l.talent.city, l.talent.instagram_handle ? `@${l.talent.instagram_handle}` : null].filter(Boolean).join(" · ");
              const betreut = l.managers.length > 0 ? t(`Betreut von ${l.managers.join(", ")}`, `Managed by ${l.managers.join(", ")}`) : "";
              return (
              <tr key={l.talent.id} className="tk-sd-zeile" onClick={(e) => {
                // D-1: die ganze Zeile öffnet das Profil; Knöpfe, Links und Häkchen behalten ihre eigene Wirkung
                const el = e.target as HTMLElement;
                if (el.closest("a") || el.closest("button") || el.closest("input") || el.closest("label")) return;
                navigate(`/agentur/creator/${l.talent.id}`);
              }}>
                {selectable.length > 0 && (
                  <td className="tk-koop-auswahl">
                    {invitable(l) && <input type="checkbox" checked={selected.has(l.talent.id)} onChange={(e) => toggleOne(l.talent.id, e.target.checked)} aria-label={t(`${l.name} zum Einladen auswählen`, `Select ${l.name} for invitation`)} />}
                  </td>
                )}
                <td className="tk-koop-kopf">
                  <Link to={`/agentur/creator/${l.talent.id}`} title={l.name}><strong>{l.name}</strong></Link><BeispielEtikett von={l.talent} />
                  <small className="tk-sd-meta" title={[meta, betreut].filter(Boolean).join(" · ")}>{meta}{betreut && <span className="tk-sd-betreut">{meta ? " · " : ""}{betreut}</span>}</small>
                  {/* D2-1: eingeklappte Spalte „E-Mail“ (schmale Liste) */}
                  {l.talent.email && <small className="tk-sd-alt" title={l.talent.email}>{l.talent.email}</small>}
                </td>
                <td className="tk-adresse tk-sd-c-mail">{l.talent.email ? <MailAdresse adresse={l.talent.email} /> : "–"}</td>
                <td data-label={t("Status", "Status")} className="tk-sd-c-status">
                  <PillView pill={statusPills(l.talent, l.access)[0]!} />
                  {l.overdue > 0 && (() => { const txt = l.overdue === 1 ? t("1 Rechnung überfällig", "1 invoice overdue") : t(`${l.overdue} Rechnungen überfällig`, `${l.overdue} invoices overdue`); return <small className="tk-sd-warn" title={txt}>{txt}</small>; })()}
                </td>
                <td data-label={t("Zugang", "Access")}>
                  <PillView pill={l.access.pill} />
                  {l.access.state === "invited" && l.access.invitation && <small>{t("gültig bis ", "valid until ")}{date(l.access.invitation.expires_at, ws.org.timezone)}</small>}
                </td>
                {sicht.amounts && <td data-label={t("Provision", "Commission")} className="tk-sd-c-provision">{bpLabel(l.commissionBp)}{l.commissionIsDefault && <small>{t("Agentur-Standard", "Agency default")}</small>}</td>}
                <td data-label={t("Kooperationen", "Deals")}><span className="num">{l.activeDeals}</span><small>{l.deals} {t("insgesamt", "in total")}</small></td>
                {sicht.financials && <td data-label={t("Umsatz netto", "Net revenue")} className="tk-sd-c-umsatz"><Money cents={l.yearGross} currency={base.currency} /></td>}
                <td className="tk-koop-aktion"><button className="tk-icon-btn" onClick={() => setEdit(l.talent)} aria-label={t(`${l.name} bearbeiten`, `Edit ${l.name}`)} title={t("Bearbeiten", "Edit")}><Icon name="edit" /></button></td>
              </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={6 + Number(sicht.amounts) + Number(sicht.financials) + Number(selectable.length > 0)}>
                  {search || filter !== "alle"
                    ? t("Keine passenden Ergebnisse. Passe die Suche oder die Filter an.", "No matching results. Adjust the search or the filters.")
                    : nurLesen ? <>{t("Noch keine Creator.", "No creators yet.")}<NurLesenHinweis /></>
                    : t("Noch keine Creator. Lege den ersten Creator an; danach kannst du Kooperationen und Verträge zuordnen.", "No creators yet. Invite the first creator; then you can assign deals and contracts.")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="source-note">
        {sorted.length === 1 ? t("1 Creator", "1 creator") : t(`${sorted.length} Creator`, `${sorted.length} creators`)} · {sicht.financials
          ? t(`Umsatz netto ist das Kooperationsvolumen ${year} (Budget, Zusatzpositionen und Agenturgebühren, verbucht und geplant), gerechnet wie auf der Übersicht.`, `Net revenue is the deal volume ${year} (budget, extra items and agency fees, booked and planned), calculated as in the overview.`)
          : HIDDEN_FINANCIALS()}
        {sicht.amounts ? t(" Provision ist der Agenturanteil; „Agentur-Standard“ folgt der Einstellung der Agentur.", " Commission is the agency share; “Agency default” follows the agency's setting.") : ""} {t("Kooperationen zählt die aktiven. „Betreut von“ unter dem Namen nennt die zugewiesenen Manager; zuweisen kann der Admin im Profil oder unter Team & Rechte.", "Deals counts the active ones. “Managed by” below the name names the assigned managers; the admin can assign them in the profile or under Team & permissions.")}{plan.data && ` ${creatorBelegungText(plan.data, active)}`}{plan.data?.testBis && ` ${testFreiText(plan.data.testBis, plan.data.planName, creatorGrenzeText(plan.data.planMaxTalents))}.`}
        {base.unconverted > 0 && sicht.financials && ` ${ohneKursText(base.unconverted)}`}
      </p>
      <DemoNote />
      {edit && <TalentDialog talent={edit} base={base} onClose={() => setEdit(null)} />}
    </main>
  );
}
